'use client';

import { zodResolver } from '@hookform/resolvers/zod';

import { useTranslations } from 'next-intl';
import { use, useActionState, useEffect } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm, useFormContext } from 'react-hook-form';

import SocialAuthList from '@/app/[locale]/(legacy)/(auth)/_components/social-auth-list';
import { registerAction } from '@/app/[locale]/(legacy)/(auth)/sign-up/(funnel)/register/action';
import {
  RegisterPayload,
  registerSchema,
} from '@/app/[locale]/(legacy)/(auth)/sign-up/(funnel)/register/types';
import ContentTransition from '@/app/[locale]/(legacy)/(auth)/sign-up/_components/content-transition';
import SignUpContentTitle from '@/app/[locale]/(legacy)/(auth)/sign-up/_components/page-title';
import { Button } from '@/app/_components/ui/button';
import { Checkbox } from '@/app/_components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/app/_components/ui/form';
import { FormSubmitButton } from '@/app/_components/ui/form-submit-button';
import { Input } from '@/app/_components/ui/input';
import analyticsManager from '@/lib/analytics';
import clientConfig from '@/lib/config/client-config';
import { setFormErrors } from '@/lib/form-action';
import { ActionState } from '@/lib/form-action/type';
import RecaptchaLegals from '@/lib/recaptcha/legal';
import { RecaptchaActions } from '@/lib/recaptcha/type';
import Routes from '@/lib/router/routes';
import Link from '@/lib/translation/components/link';
import { useRouter } from '@/lib/translation/hooks';

const RegisterForm = ({
  error,
  isPending,
}: {
  error?: string;
  isPending: boolean;
}) => {
  const t = useTranslations();

  const router = useRouter();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const form = useFormContext();

  useEffect(() => {
    async function getToken() {
      if (executeRecaptcha) {
        const token = await executeRecaptcha(RecaptchaActions.SignUp);
        form.setValue('gRecaptchaToken', token);
      }
    }
    getToken();
  }, [form, executeRecaptcha]);

  return (
    <div className="flex flex-col gap-4">
      <FormField
        control={form.control}
        name="email"
        disabled={isPending}
        required
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('auth.signUp.steps.register.form.email')}</FormLabel>
            <FormControl>
              <Input
                placeholder={t('auth.signUp.steps.register.form.email')}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="password"
        disabled={isPending}
        required
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t('auth.signUp.steps.register.form.password')}
            </FormLabel>

            <FormControl>
              <Input
                placeholder={t('auth.signUp.steps.register.form.password')}
                type="password"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="acceptRiskCgu"
        disabled={isPending}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <div className="flex flex-row gap-4">
                <Checkbox
                  id={field.name}
                  name={field.name}
                  checked={field.value === 'on'}
                  onCheckedChange={(value) => {
                    field.onChange(value === true ? 'on' : null);
                  }}
                  data-testid="register-cgu-checkbox"
                />

                <label
                  htmlFor={field.name}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t('auth.signUp.steps.register.form.acceptRiskCgu')}
                  <Link
                    href="https://sowefund.com/cgu"
                    className="text-primary underline"
                    target="_blank"
                  >
                    {t('auth.signUp.steps.register.form.acceptRiskCguLabel')}
                  </Link>
                  {', '}
                  <Link
                    href="https://sowefund.com/politique-protection-donnee"
                    className="text-primary underline"
                    target="_blank"
                  >
                    {t(
                      'auth.signUp.steps.register.form.acceptRiskPrivacyPolicyLabel',
                    )}
                  </Link>
                  {t('auth.signUp.steps.register.form.acceptRiskCguAnd')}
                  <Link
                    href="http://sowefund.com/cgu-lemonway"
                    className="text-primary underline"
                    target="_blank"
                  >
                    {t(
                      'auth.signUp.steps.register.form.acceptRiskCguLemonWayLabel',
                    )}
                  </Link>
                </label>
              </div>
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="acceptRiskWarning"
        disabled={isPending}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <div className="flex flex-row gap-4">
                <Checkbox
                  id={field.name}
                  name={field.name}
                  checked={field.value === 'on'}
                  onCheckedChange={(value) => {
                    field.onChange(value === true ? 'on' : 'off');
                  }}
                  data-testid="register-risk-checkbox"
                />

                <label
                  htmlFor={field.name}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t('auth.signUp.steps.register.form.acceptRiskWarning')}
                  <Link
                    href="https://sowefund.com/mentions-legales#risques-des-investissements-en-actions-non-cotees-de-societes-en-phase-damorcage"
                    className="text-primary underline"
                    target="_blank"
                  >
                    {t(
                      'auth.signUp.steps.register.form.acceptRiskWarningLabel',
                    )}
                  </Link>
                </label>
              </div>
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="acceptsNewsLetter"
        disabled={isPending}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <div className="flex flex-row gap-4">
                <Checkbox
                  id={field.name}
                  name={field.name}
                  checked={field.value === 'on'}
                  onCheckedChange={(value) => {
                    field.onChange(value === true ? 'on' : 'off');
                  }}
                />

                <label
                  htmlFor={field.name}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t('auth.signUp.steps.register.form.acceptsNewsLetter')}
                </label>
              </div>
            </FormControl>
          </FormItem>
        )}
      />

      <RecaptchaLegals />

      <FormField
        control={form.control}
        name="gRecaptchaToken"
        render={({ field }) => <Input type="hidden" {...field} />}
      />
      {form.formState.errors.root ? (
        <p className="text-sm font-bold text-destructive">
          {form.formState.errors.root.message}
        </p>
      ) : null}
      {error ? (
        <p className="text-sm font-bold text-destructive">{error}</p>
      ) : null}

      <FormField
        control={form.control}
        name="origin"
        render={({ field }) => <Input type="hidden" {...field} />}
      />

      <div className="mt-6 flex w-fit flex-col gap-y-4">
        <FormSubmitButton
          control={form.control}
          pending={isPending}
          className="w-full"
        >
          {t('auth.signUp.steps.register.form.submit')}
        </FormSubmitButton>

        <Button
          className="w-full"
          variant="ghost"
          disabled={isPending}
          onClick={() => router.push(Routes.SignIn)}
          type="button"
        >
          {t('auth.signUp.steps.register.form.secondary')}
        </Button>
      </div>
    </div>
  );
};

interface RegisterProps {
  searchParams: Promise<{
    email?: string;
    error?: string;
    origin?: string;
  }>;
}

const RegisterContent = ({ searchParams }: RegisterProps) => {
  const t = useTranslations();
  const router = useRouter();

  const { email, error, origin } = use(searchParams);

  const form = useForm<RegisterPayload>({
    resolver: zodResolver(registerSchema(t)),
    defaultValues: {
      email: email ?? '',
      password: '',
      acceptRiskWarning: 'off',
      acceptRiskCgu: 'off',
      acceptsNewsLetter: 'off',
      gRecaptchaToken: '',
      origin: origin,
    },
    mode: 'all',
  });

  const [, formAction, isPending] = useActionState<
    ActionState<RegisterPayload>,
    FormData
  >(async (state, payload) => {
    const newState = await registerAction(state, payload);

    if (!newState) {
      return null;
    }

    if (newState.status === 'error' && newState.errors) {
      setFormErrors(newState.errors, form);
    } else if (newState.status === 'success') {
      analyticsManager.client.sendEvent({
        event: 'sign_up',
        method: 'Email',
      });
      router.push(Routes.SignUpIdentity);
      // Here we need to refresh the page to let the root layout refresh properly.
      // We sometines need the user data client side to be properly computed,
      // Hence, we force the render of the root layout
      router.refresh();
    }

    return newState;
  }, null);

  const hasSearchParams = email !== undefined || error !== undefined;

  return (
    <ContentTransition>
      {hasSearchParams && <meta name="robots" content="noindex, nofollow" />}
      <SignUpContentTitle className="mb-16">
        {t('auth.signUp.steps.register.form.title')}
      </SignUpContentTitle>

      <Form {...form}>
        <form action={formAction}>
          <RegisterForm error={error} isPending={isPending} />
        </form>
      </Form>

      {clientConfig.featureFlag.isSocialLoginEnabled ? (
        <SocialAuthList
          callbackUrl={Routes.SignUpIdentity}
          sectionTitle={t('auth.signUp.steps.register.socialLogin')}
          context="signUp"
        />
      ) : null}
    </ContentTransition>
  );
};

export default RegisterContent;
